// WbVehiclesPage — /weighbridge/vehicles (admin only).
//
// Phase C3 Group 2. Paginated list of tenant vehicles with server-side
// registration search (filter token, partial LIKE match). Row click
// navigates to the detail/edit page; "+ Add Vehicle" routes to /new.
//
// Server contract: GET /wb/vehicles?page&per_page&registration&haulier_id
// &is_active → { vehicles, pagination }. Tare status is a computed field
// on the DTO — see useWbVehicles.ts for the type contract.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Truck } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Badge } from '../../../components/ui/Badge/Badge';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import {
  useWbVehicles,
  type TareStatus,
  type VehicleType,
  type WbVehicle,
} from '../hooks/useWbVehicles';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbVehiclesPage.module.css';

const PER_PAGE = 50;

const TYPE_LABEL: Record<VehicleType, string> = {
  rigid: 'Rigid',
  artic: 'Artic',
  van: 'Van',
  skip: 'Skip',
  roro: 'RoRo',
  other: 'Other',
};

function formatKg(value: number): string {
  return `${value.toLocaleString()} kg`;
}

interface TareBadgeProps {
  status: TareStatus;
  storedTareKg: number | null;
}

function TareBadge({ status, storedTareKg }: TareBadgeProps): JSX.Element {
  if (status === 'fresh' && storedTareKg !== null) {
    return <Badge variant="success">{formatKg(storedTareKg)}</Badge>;
  }
  if (status === 'stale') {
    return <Badge variant="warning">Stale</Badge>;
  }
  return <Badge variant="muted">—</Badge>;
}

export function WbVehiclesPage(): JSX.Element {
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce the search input 300ms; reset to page 1 whenever the
  // debounced value changes so we never end up viewing page 5 of a
  // freshly-filtered set that only has one page.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = useMemo(
    () => ({
      page,
      per_page: PER_PAGE,
      ...(debouncedSearch ? { registration: debouncedSearch } : {}),
    }),
    [page, debouncedSearch],
  );

  const vehiclesQuery = useWbVehicles(params);
  const vehicles = vehiclesQuery.data?.vehicles ?? [];
  const pagination = vehiclesQuery.data?.pagination;
  const totalPages = pagination?.total_pages ?? 1;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Vehicles"
        subtitle="Tractor units, trailers and other moving assets that cross the weighbridge."
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/weighbridge/vehicles/new')}
          >
            Add Vehicle
          </Button>
        }
      />

      <div className={styles.toolbar}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search by registration"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search by registration"
        />
      </div>

      {vehiclesQuery.isError ? (
        <p className={styles.error}>{extractErrorMessage(vehiclesQuery.error)}</p>
      ) : null}

      <Card flush>
        {vehiclesQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading vehicles" />
          </div>
        ) : vehicles.length === 0 ? (
          <EmptyState
            icon={Truck}
            title={debouncedSearch ? 'No vehicles match that search' : 'No vehicles yet'}
            description={
              debouncedSearch
                ? 'Try a different registration or clear the search.'
                : 'Add a vehicle to start associating it with weighbridge tickets.'
            }
            action={
              debouncedSearch ? undefined : (
                <Button
                  variant="primary"
                  icon={Plus}
                  onClick={() => navigate('/weighbridge/vehicles/new')}
                >
                  Add Vehicle
                </Button>
              )
            }
          />
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>VRM</th>
                  <th>Type</th>
                  <th>Haulier</th>
                  <th>Tare status</th>
                  <th>Max capacity</th>
                  <th>Active</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v: WbVehicle) => (
                  <tr
                    key={v.enc_id}
                    onClick={() =>
                      navigate(`/weighbridge/vehicles/${encodeURIComponent(v.enc_id)}`)
                    }
                  >
                    <td>
                      <span className={styles.vrm}>{v.registration}</span>
                    </td>
                    <td>{TYPE_LABEL[v.vehicle_type]}</td>
                    <td>
                      {v.haulier ? (
                        v.haulier.name
                      ) : (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                    <td>
                      <TareBadge
                        status={v.tare_status}
                        storedTareKg={v.stored_tare_kg}
                      />
                    </td>
                    <td>
                      {v.max_capacity_kg !== null ? (
                        formatKg(v.max_capacity_kg)
                      ) : (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                    <td>
                      {v.is_active ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="muted">Inactive</Badge>
                      )}
                    </td>
                    <td className={styles.actionsCell}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/weighbridge/vehicles/${encodeURIComponent(v.enc_id)}`,
                          );
                        }}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination && totalPages > 1 ? (
              <div className={styles.pagination}>
                <span className={styles.pageInfo}>
                  Page {pagination.page} of {pagination.total_pages} ·{' '}
                  {pagination.total.toLocaleString()} vehicle
                  {pagination.total === 1 ? '' : 's'}
                </span>
                <div className={styles.pageButtons}>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}
